import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { can } from "@/lib/permissions";

import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Depot from "@/pages/operations/Depot";
import Restitution from "@/pages/operations/Restitution";
import Delivrance from "@/pages/operations/Delivrance";
import OperationDetail from "@/pages/operations/OperationDetail";
import Recherche from "@/pages/Recherche";
import Profil from "@/pages/Profil";
import Queue from "@/pages/Queue";
import ClientList from "@/pages/clients/ClientList";
import ClientForm from "@/pages/clients/ClientForm";
import ClientDetail from "@/pages/clients/ClientDetail";
import ClientImport from "@/pages/clients/ClientImport";
import AdminUsers from "@/pages/admin/Users";
import AdminIcons from "@/pages/admin/Icons";
import AdminBackupEmails from "@/pages/admin/BackupEmails";
import AdminContacts from "@/pages/admin/Contacts";
import AdminPurge from "@/pages/admin/Purge";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--vidoc-text-secondary)]">
        Chargement…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/delivrance" replace />;
  return children;
}

function RequirePermission({ action, children, fallback = "/delivrance" }) {
  const { user } = useAuth();
  if (!can(user, action)) return <Navigate to={fallback} replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingProvider>
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route
              element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }
            >
              <Route path="/depot" element={<RequirePermission action="depot"><Depot /></RequirePermission>} />
              <Route path="/restitution" element={<RequirePermission action="restitution"><Restitution /></RequirePermission>} />
              <Route path="/delivrance" element={<RequirePermission action="delivrance"><Delivrance /></RequirePermission>} />
              <Route path="/operations/:id" element={<RequirePermission action="client.read"><OperationDetail /></RequirePermission>} />

              <Route path="/recherche" element={<RequirePermission action="search.global"><Recherche /></RequirePermission>} />
              <Route path="/profil" element={<Profil />} />
              <Route path="/queue" element={<Queue />} />

              <Route path="/clients" element={<RequirePermission action="client.list"><ClientList /></RequirePermission>} />
              <Route path="/clients/add" element={<RequirePermission action="client.create"><ClientForm mode="add" /></RequirePermission>} />
              <Route path="/clients/import" element={<RequirePermission action="client.import"><ClientImport /></RequirePermission>} />
              <Route path="/clients/:id" element={<RequirePermission action="client.read"><ClientDetail /></RequirePermission>} />
              <Route path="/clients/:id/edit" element={<RequirePermission action="client.update"><ClientForm mode="edit" /></RequirePermission>} />

              <Route
                path="/admin/users"
                element={
                  <RequireAdmin>
                    <AdminUsers />
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/icons"
                element={
                  <RequireAdmin>
                    <AdminIcons />
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/backup-emails"
                element={
                  <RequireAdmin>
                    <AdminBackupEmails />
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/contacts"
                element={
                  <RequireAdmin>
                    <AdminContacts />
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/purge"
                element={
                  <RequireAdmin>
                    <AdminPurge />
                  </RequireAdmin>
                }
              />

              <Route index element={<Navigate to="/delivrance" replace />} />
            </Route>

            <Route path="*" element={<Navigate to="/delivrance" replace />} />
          </Routes>
        </BrandingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
